import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  fitHeaderTitle,
  formatQuoteDate,
  formatCurrency,
  getDataUrl,
  renderFooter,
  clientDocumentLabel,
  renderLogoWatermark,
  buildItemRows,
} from './shared';
import { numeroParaReais } from '@/lib/valorExtenso';

/**
 * Template de RECIBO (documentType === 'recibo').
 *
 * A espinha do documento é a frase clássica — "Recebi(emos) de [pagador] a
 * importância de [valor por extenso] referente a [descrição]" — com número,
 * valor em destaque, forma de pagamento, local + data e a assinatura OPCIONAL
 * do emitente. `total` carrega o valor do recibo.
 *
 * ─── A TABELA DE ITENS É OPCIONAL (lacuna L1) ───────────────────────────────
 *
 * Quando o recibo tem linhas discriminadas, elas são impressas ENTRE a caixa de
 * valor e a frase, com a mesma tabela dos outros documentos (`buildItemRows`).
 * Sem linhas, **nada é desenhado e nenhuma coordenada muda** — é o que mantém
 * todo recibo já emitido byte a byte igual (a disciplina do #202/#104).
 *
 * A frase permanece nos dois casos: é ela que dá quitação, e a tabela apenas
 * discrimina o que foi pago.
 */
export interface ReciboRenderOptions {
  /**
   * Primeira página deste recibo, quando ele é um entre vários no mesmo PDF
   * (exportação em massa). Repassado ao rodapé para não carimbar o número deste
   * recibo em cima das páginas dos anteriores.
   */
  fromPage?: number;
}

export async function render(
  doc: jsPDF,
  data: PDFQuoteData,
  opts: ReciboRenderOptions = {},
): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;
  const bottomLimit = pageHeight - 20;
  const { primaryColor, grayVeryLight, grayText, darkText, white } = buildPalette(data.brandColorRGB);

  const logoBase64 = await loadLogo(data.company?.logoUrl);
  const amount = Number(data.total ?? 0);

  // === HEADER ===
  const headerHeight = 40;
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  doc.setTextColor(...white);
  doc.setFont('helvetica', 'bold');
  const tituloCabecalho = fitHeaderTitle(doc, resolveDocumentTitle(data), pageWidth - margin * 2, 22);
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, margin, 18);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(data.company?.name || 'Minha Empresa', margin, 27);
  if (data.company?.cnpj) {
    doc.setFontSize(9);
    doc.text(`CNPJ/CPF: ${data.company.cnpj}`, margin, 33);
  }

  if (logoBase64) {
    try {
      const logoBoxSize = 24;
      doc.addImage(
        logoBase64,
        getJsPdfImageFormat(logoBase64),
        pageWidth - margin - logoBoxSize,
        8,
        logoBoxSize,
        logoBoxSize,
      );
    } catch {
      /* sem logo — segue sem placeholder no recibo */
    }
  }

  let yPos = headerHeight + 12;

  // === NÚMERO + VALOR EM DESTAQUE ===
  const numeroLabel =
    data.showSequenceId && data.sequenceId
      ? `RECIBO Nº ${String(data.sequenceId).padStart(4, '0')}`
      : 'RECIBO';
  const valueBoxH = 22;
  doc.setFillColor(...grayVeryLight);
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, yPos, contentWidth, valueBoxH, 2, 2, 'FD');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text(numeroLabel, margin + 5, yPos + 9);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Valor recebido', margin + 5, yPos + 15);

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text(formatCurrency(amount), pageWidth - margin - 5, yPos + 13, { align: 'right' });

  yPos += valueBoxH + 12;

  /*
   * TABELA DE ITENS — só quando o recibo tem linhas.
   *
   * `data.items` já existe em `PDFQuoteData` e é o mesmo formato dos outros
   * documentos, então a tabela sai de `buildItemRows` e não há uma segunda
   * fórmula de total no app (#69). Recibo sem linhas não desenha nada e o
   * `yPos` não anda — é o que preserva o histórico byte a byte.
   */
  const itensDoRecibo = data.items ?? [];
  if (itensDoRecibo.length > 0) {
    const { hasAnyDiscount, headers, body } = buildItemRows(itensDoRecibo);
    // Mesmas larguras do `classico`: o recibo compartilha a tabela, então
    // compartilhar a geometria evita duas tabelas com aparências diferentes.
    const columnStyles = hasAnyDiscount
      ? {
          0: { cellWidth: 'auto' as const, halign: 'left' as const },
          1: { cellWidth: 16, halign: 'center' as const },
          2: { cellWidth: 14, halign: 'center' as const },
          3: { cellWidth: 28, halign: 'right' as const },
          4: { cellWidth: 22, halign: 'center' as const },
          5: { cellWidth: 30, halign: 'right' as const },
        }
      : {
          0: { cellWidth: 'auto' as const, halign: 'left' as const },
          1: { cellWidth: 18, halign: 'center' as const },
          2: { cellWidth: 14, halign: 'center' as const },
          3: { cellWidth: 32, halign: 'right' as const },
          4: { cellWidth: 32, halign: 'right' as const },
        };
    autoTable(doc, {
      startY: yPos,
      head: headers,
      body,
      headStyles: {
        fillColor: primaryColor,
        textColor: white,
        fontStyle: 'bold',
        fontSize: 8.5,
        halign: 'center',
        cellPadding: 3,
      },
      columnStyles,
      bodyStyles: { fontSize: 8, cellPadding: 2.5 },
      alternateRowStyles: { fillColor: grayVeryLight },
      styles: { lineColor: [200, 200, 200], lineWidth: 0.3 },
      theme: 'grid',
      margin: { left: margin, right: margin },
    });
    yPos =
      (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  }

  // === CORPO (frase de recibo) ===
  const extenso = numeroParaReais(amount);
  const payer = data.client?.name?.trim() || 'Não informado';
  const payerDoc = data.client?.document ? ` (${clientDocumentLabel(data.client.document)})` : '';
  const descricao = data.receiptDescription?.trim() || '—';

  const phrase =
    `Recebi(emos) de ${payer}${payerDoc} a importância de ${extenso} ` +
    `(${formatCurrency(amount)}), referente a ${descricao}.`;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  const lineHeight = 6;
  const phraseLines = doc.splitTextToSize(phrase, contentWidth) as string[];
  for (const line of phraseLines) {
    if (yPos > bottomLimit) {
      doc.addPage();
      yPos = 20;
    }
    doc.text(line, margin, yPos);
    yPos += lineHeight;
  }
  yPos += 3;

  // Forma de pagamento
  if (data.receiptPaymentMethodLabel?.trim()) {
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text('Forma de pagamento: ', margin, yPos);
    const labelW = doc.getTextWidth('Forma de pagamento: ');
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(data.receiptPaymentMethodLabel, margin + labelW, yPos);
    yPos += 8;
  }

  // Condições combinadas ("3x sem juros"). Ausente não desenha nada.
  if (data.receiptPaymentTerms?.trim()) {
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text('Condições: ', margin, yPos);
    const condW = doc.getTextWidth('Condições: ');
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    const condLines = doc.splitTextToSize(
      data.receiptPaymentTerms.trim(),
      contentWidth - condW,
    ) as string[];
    doc.text(condLines[0], margin + condW, yPos);
    yPos += 8;
  }

  // Quitação (frase de praxe)
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  const quitLines = doc.splitTextToSize(
    'Para maior clareza, firmo(amos) o presente recibo, dando plena, geral e irrevogável quitação do valor acima.',
    contentWidth,
  ) as string[];
  for (const line of quitLines) {
    if (yPos > bottomLimit) {
      doc.addPage();
      yPos = 20;
    }
    doc.text(line, margin, yPos);
    yPos += 5;
  }
  yPos += 3;

  // Observações (opcional)
  if (data.receiptNotes?.trim()) {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...grayText);
    const noteLines = doc.splitTextToSize(`Obs.: ${data.receiptNotes.trim()}`, contentWidth) as string[];
    for (const line of noteLines) {
      if (yPos > bottomLimit) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(line, margin, yPos);
      yPos += 4.5;
    }
    yPos += 3;
  }

  // === LOCAL + DATA ===
  const place = data.receiptIssuePlace?.trim();
  const dateStr = formatQuoteDate(data.quoteDate);
  const localData = place ? `${place}, ${dateStr}.` : `${dateStr}.`;
  yPos += 6;
  if (yPos > bottomLimit - 40) {
    doc.addPage();
    yPos = 24;
  }
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.text(localData, pageWidth - margin, yPos, { align: 'right' });

  // === ASSINATURA DO EMITENTE ===
  yPos += 22;
  const sigWidth = 84;
  const sigX = pageWidth / 2 - sigWidth / 2;

  // Imagem da assinatura (se houver), acima da linha.
  if (data.issuerSignatureDataUrl) {
    try {
      const img = await getDataUrl(data.issuerSignatureDataUrl);
      if (img) {
        const sigImgH = 20;
        doc.addImage(img, getJsPdfImageFormat(img), sigX, yPos - sigImgH, sigWidth, sigImgH);
      }
    } catch {
      /* segue sem a imagem — a linha e o nome abaixo ainda comprovam */
    }
  }

  doc.setDrawColor(...grayText);
  doc.setLineWidth(0.3);
  doc.line(sigX, yPos, sigX + sigWidth, yPos);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkText);
  const signerName = data.issuerSignerName?.trim() || data.company?.name || 'Emitente';
  doc.text(signerName, pageWidth / 2, yPos + 5, { align: 'center' });

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  const emitLabel = data.company?.cnpj ? `Emitente · CNPJ/CPF: ${data.company.cnpj}` : 'Emitente';
  doc.text(emitLabel, pageWidth / 2, yPos + 9, { align: 'center' });

  // === FOOTER (branding + sequência) ===
  renderFooter(doc, data, {
    primaryColor,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    fromPage: opts.fromPage,
  });
}
